import { Button } from '@finn/design-system/atoms/button';
import { Accordion, RawHtml } from '@finn/platform-modules';
import { AvailableFilters } from '@finn/ua-vehicle';
import { AccordionCosmicMetadata } from '@finn/ui-cosmic';
import get from 'lodash/get';

import CategoriesBanner from '~/components/CategoriesBanner';
import InfoBox from '~/components/InfoBox';
import { SmallSpacingBox } from '~/components/SpacingBox';
import {
  BaseCosmicData,
  CosmicMetafield,
  CosmicMetafieldType,
  FaqSection,
} from '~/types/cosmicModules';
import BrandSlider from '~/ui-modules/BrandSlider';
import { FormWrapper } from '~/ui-modules/ContactForm/B2BContactForm/FormWrapper';

type Props = {
  block: CosmicMetafield;
  content: string | BaseCosmicData;
  nextAvailableFilters: AvailableFilters;
};

const CosmicBlock = ({ block, content, nextAvailableFilters }: Props) => {
  // Check & Render products slider
  const shouldRenderProductSlider =
    block.type === CosmicMetafieldType.object &&
    block.object_type === 'sliders';
  if (shouldRenderProductSlider) {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
    };

    return (
      <SmallSpacingBox>
        <div className="container">
          <Button onClick={scrollToTop}>
            {get(content, 'metadata.title', '')}
          </Button>
        </div>
      </SmallSpacingBox>
    );
  }

  // Check & Render brands slider
  const shouldRenderBrandSlider =
    block.type === CosmicMetafieldType.object &&
    block.object_type === 'brand-sliders';
  if (shouldRenderBrandSlider) {
    return (
      <SmallSpacingBox>
        <BrandSlider
          brands={get(nextAvailableFilters, 'brands', [])}
          title=""
        />
        ;
      </SmallSpacingBox>
    );
  }

  // Check & Render B2B Contact Form
  const shouldRenderB2BContactForm =
    block.type === CosmicMetafieldType.object &&
    block.object_type === 'contact-forms';
  if (shouldRenderB2BContactForm) {
    // previously types was any that was coming from formik, so need to add any
    // to emulate the same till we refactor
    return <FormWrapper cosmicData={content as any} />;
  }

  // Check & Render info box
  const shouldRenderInfoBox =
    block.type === CosmicMetafieldType.object &&
    block.object_type === 'info-boxes';
  if (shouldRenderInfoBox) {
    return (
      <SmallSpacingBox noPaddingBottom>
        <div className="container">
          <InfoBox
            title={get(content, 'metadata.title', '')}
            description={get(content, 'metadata.description', '')}
          />
        </div>
      </SmallSpacingBox>
    );
  }

  // Check & Render cartype
  const shouldRenderCartypeBanner =
    block.type === CosmicMetafieldType.object &&
    block.object_type === 'cartype-banners';
  if (shouldRenderCartypeBanner) {
    return (
      <CategoriesBanner
        title={get(content, 'metadata.title')}
        availableCartypes={nextAvailableFilters.cartypes}
        categories={get(content, 'metadata.cartypes', [])}
      />
    );
  }

  // Check & Render FAQ
  const shouldRenderFAQ =
    block.type === CosmicMetafieldType.parent && block.key === 'faq';

  if (shouldRenderFAQ) {
    const faqData = (content || {}) as FaqSection;

    const data = {
      title: faqData.title,
      items: faqData.questions.map((item) => ({
        ...item,
        metadata: {
          title: item.metadata.question,
          description: item.metadata.answer,
        },
      })),
    };

    return faqData ? (
      <div className="mt-16" data-testid="plp-advisory-faq">
        <Accordion data={data as unknown as AccordionCosmicMetadata} isInPLP />
      </div>
    ) : null;
  }

  // Check & Render HTML raw
  const shouldRenderHTMLRaw = block.type === CosmicMetafieldType.htmlTextarea;
  if (shouldRenderHTMLRaw) {
    content = (content as string).replaceAll(
      '<h2',
      `<h2 class="!web-t4-semibold !mt-20"`
    );
    content = (content as string).replaceAll(
      '<h3',
      `<h3 class="!global-t5-semibold !mt-16"`
    );

    return (
      <div className="container" data-testid="plp-advisory-content">
        <RawHtml html={content as string} useGlobalStyles fullWidthMedia />
      </div>
    );
  }

  // Don't render anything if object type not supported yet
  return null;
};

export default CosmicBlock;
